import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Building2, DollarSign, TrendingDown, TrendingUp } from "lucide-react";

import { usePageTitle } from "@/hooks/usePageTitle";
import { useLocale } from "@/hooks/useLocale";
import { withBranding } from "@/utils/helper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, type ColumnDef } from "@/components/table";
import { useGetPayrollReportSummaryQuery } from "@/services/(company)/payrollReportsApi";
import { useGetBranchesQuery } from "@/services/(company)/branchesApi";
import type {
  IBranchRow,
  IMonthRow,
  IPaymentMethodRow,
} from "@/services/(company)/payrollReportsApi";
import { useTranslation } from "react-i18next";

const BRANCH_ALL = "all" as const;

const buildYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => ({
    value: String(current - i),
    label: String(current - i),
  }));
};

const formatAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TONE_ACCENT: Record<string, string> = {
  primary: "var(--color-primary)",
  success: "var(--color-success)",
  danger: "var(--color-danger)",
  info: "var(--color-info)",
};

const StatCard = ({
  label,
  value,
  currency,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  currency: string;
  icon: React.ElementType;
  tone: "primary" | "success" | "danger" | "info";
}) => {
  const accent = TONE_ACCENT[tone] ?? "var(--color-primary)";
  return (
    <div className="relative rounded-sm border border-border bg-white p-4 flex flex-col gap-2 overflow-hidden hover:shadow-xs transition-all">
      <span
        className="absolute inset-x-0 top-0 h-[3px] rounded-t-sm"
        style={{ backgroundColor: accent }}
      />
      <div className="flex items-center gap-2">
        <div
          className="size-8 rounded-sm flex items-center justify-center shrink-0"
          style={{
            backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
          }}
        >
          <Icon size={15} style={{ color: accent }} />
        </div>
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
          {label}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-gray-400">{currency}</span>
        <span className="text-xl font-bold text-gray-900 tabular-nums leading-none">
          {formatAmount(value)}
        </span>
      </div>
    </div>
  );
};

export const PayrollSummary = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("company:payrolls.payroll_summary")));

  const yearOptions = useMemo(buildYearOptions, []);
  const [year, setYear] = useState<string>(String(new Date().getFullYear()));
  const [branchFilter, setBranchFilter] = useState<string>(BRANCH_ALL);

  const branchId =
    branchFilter !== BRANCH_ALL ? Number(branchFilter) : undefined;

  const { data: branchesResp } = useGetBranchesQuery({ page: 1, limit: 100 });
  const branchOptions = useMemo(() => branchesResp?.data ?? [], [branchesResp]);

  const { data, isFetching } = useGetPayrollReportSummaryQuery({
    year: Number(year),
    ...(branchId ? { branchId } : {}),
  });

  const summary = data?.data;
  const primary = summary?.ytdTotals?.[0];
  const currency = primary?.currencyCode ?? "AED";
  const hasData = !!summary && summary.ytdTotals.length > 0;

  // `monthLabel` comes from the API pre-formatted in English (e.g. "Jan") —
  // localize it client-side from the numeric `month` field it ships alongside,
  // the same way every other date in the app goes through useLocale().
  const localizedByMonth = useMemo(() => {
    const all = summary?.byMonth ?? [];
    return all.map((m) => ({
      ...m,
      monthLabel: formatDate(new Date(Number(year), m.month - 1, 1), "MMM"),
    }));
  }, [summary, year, formatDate]);

  const monthsWithData = useMemo(() => {
    let lastIdx = -1;
    localizedByMonth.forEach((m, i) => {
      if (m.gross > 0 || m.net > 0 || m.employeeCount > 0) lastIdx = i;
    });
    return lastIdx >= 0 ? localizedByMonth.slice(0, lastIdx + 1) : [];
  }, [localizedByMonth]);

  const methodCell = (amount: number) =>
    amount > 0 ? (
      <span className="text-sm text-gray-700">
        {currency} {formatAmount(amount)}
      </span>
    ) : (
      <span className="text-sm text-gray-300">—</span>
    );

  const monthColumns: ColumnDef<IMonthRow>[] = [
    {
      key: "monthLabel",
      label: t("company:payrolls.month"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.monthLabel}
        </span>
      ),
    },
    {
      key: "employeeCount",
      label: t("company:payrolls.employees_short"),
      className: "w-[8%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.employeeCount || "—"}</span>
      ),
    },
    {
      key: "gross",
      label: t("company:payrolls.gross"),
      className: "w-[15%]",
      render: (r) => (
        <span className="text-sm text-gray-900">
          {r.gross > 0
            ? `${r.currencyCode ?? currency} ${formatAmount(r.gross)}`
            : "—"}
        </span>
      ),
    },
    {
      key: "deductions",
      label: t("company:payrolls.deductions"),
      className: "w-[15%]",
      render: (r) => (
        <span className="text-sm text-red-500">
          {r.deductions > 0
            ? `${r.currencyCode ?? currency} ${formatAmount(r.deductions)}`
            : "—"}
        </span>
      ),
    },
    {
      key: "net",
      label: t("company:payrolls.net_short"),
      className: "w-[15%]",
      render: (r) => (
        <span className="text-sm font-semibold text-success-100">
          {r.net > 0
            ? `${r.currencyCode ?? currency} ${formatAmount(r.net)}`
            : "—"}
        </span>
      ),
    },
    {
      key: "wps",
      label: t("company:payrolls.wps_label"),
      className: "w-[12%]",
      render: (r) => methodCell(r.byMethod?.WPS ?? 0),
    },
    {
      key: "bank",
      label: t("company:payrolls.bank_transfer"),
      className: "w-[12%]",
      render: (r) => methodCell(r.byMethod?.BANK_TRANSFER ?? 0),
    },
    {
      key: "cash",
      label: t("company:payrolls.cash"),
      className: "w-[12%]",
      render: (r) => methodCell(r.byMethod?.CASH ?? 0),
    },
  ];

  const branchColumns: ColumnDef<IBranchRow>[] = [
    {
      key: "branchName",
      label: t("common:ui_kit.branch"),
      className: "w-[35%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.branchName}
        </span>
      ),
    },
    {
      key: "employeeCount",
      label: t("company:payrolls.employees"),
      className: "w-[15%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.employeeCount}</span>
      ),
    },
    {
      key: "gross",
      label: t("company:payrolls.gross"),
      className: "w-[25%]",
      render: (r) => (
        <span className="text-sm text-gray-900">
          {r.currencyCode ?? "AED"} {formatAmount(r.gross)}
        </span>
      ),
    },
    {
      key: "net",
      label: t("company:payrolls.net_short"),
      className: "w-[25%]",
      render: (r) => (
        <span className="text-sm font-semibold text-success-100">
          {r.currencyCode ?? "AED"} {formatAmount(r.net)}
        </span>
      ),
    },
  ];

  const methodColumns: ColumnDef<IPaymentMethodRow>[] = [
    {
      key: "label",
      label: t("company:payrolls.method"),
      className: "w-[40%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">{r.label}</span>
      ),
    },
    {
      key: "employeeCount",
      label: t("company:payrolls.employees"),
      className: "w-[25%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.employeeCount}</span>
      ),
    },
    {
      key: "net",
      label: t("company:payrolls.net_short"),
      className: "w-[35%]",
      render: (r) => (
        <span className="text-sm font-semibold text-success-100">
          {r.currencyCode ?? "AED"} {formatAmount(r.net)}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
              <SelectValue placeholder={t("common:shared_ui.year")} />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
              <SelectValue placeholder={t("common:ui_kit.branch")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BRANCH_ALL}>
                {t("common:ui_kit.all_branches")}
              </SelectItem>
              {branchOptions.map((b: any) => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isFetching && !hasData && (
        <div className="rounded-xs border border-yellow-200 bg-yellow-50 px-4 py-3 text-xs text-yellow-800">
          {t("company:payrolls.no_finalized_runs_found", {
            year,
            branchSuffix:
              branchFilter !== BRANCH_ALL
                ? t("company:payrolls.in_selected_branch")
                : "",
          })}
        </div>
      )}

      {/* YTD stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label={t("company:payrolls.total_cost")}
          value={primary?.gross ?? 0}
          currency={currency}
          icon={DollarSign}
          tone="primary"
        />
        <StatCard
          label={t("company:payrolls.net_pay")}
          value={primary?.net ?? 0}
          currency={currency}
          icon={TrendingUp}
          tone="success"
        />
        <StatCard
          label={t("company:payrolls.deductions")}
          value={primary?.deductions ?? 0}
          currency={currency}
          icon={TrendingDown}
          tone="danger"
        />
        <StatCard
          label={t("company:payrolls.employer_cost")}
          value={primary?.employerCost ?? 0}
          currency={currency}
          icon={Building2}
          tone="info"
        />
      </div>

      {/* Month-over-month chart */}
      <div className="rounded-xs border border-border bg-white p-4">
        <div className="flex flex-col gap-1 mb-3">
          <span className="text-sm font-semibold text-gray-900">
            {t("company:payrolls.month_over_month")}
          </span>
          <span className="text-xs text-gray-500">
            {t("company:payrolls.gross_vs_net_per_month", { year })}
          </span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={localizedByMonth}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="monthLabel"
                tick={{ fontSize: 11 }}
                stroke="#94a3b8"
              />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip
                formatter={(v: number) =>
                  `${currency} ${formatAmount(Number(v))}`
                }
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="gross"
                fill="#6438ff"
                name={t("company:payrolls.gross")}
              />
              <Bar
                dataKey="net"
                fill="#1ad598"
                name={t("company:payrolls.net_short")}
              />
              <Bar
                dataKey="deductions"
                fill="#ef4444"
                name={t("company:payrolls.deductions")}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly breakdown table */}
      <div className="rounded-xs border border-border bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold text-gray-900">
            {t("company:payrolls.monthly_breakdown")}
          </span>
          {primary && (
            <span className="text-xs text-gray-500">
              {t("company:payrolls.year_total_net_gross", {
                net: `${currency} ${formatAmount(primary.net)}`,
                gross: `${currency} ${formatAmount(primary.gross)}`,
              })}
            </span>
          )}
        </div>
        <Table<IMonthRow>
          columns={monthColumns}
          data={monthsWithData}
          loading={isFetching}
          emptyMessage={t("company:payrolls.no_monthly_data")}
        />
      </div>

      {/* By branch + by payment method */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xs border border-border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-gray-900">
              {t("company:payrolls.by_branch")}
            </span>
          </div>
          <Table<IBranchRow>
            columns={branchColumns}
            data={summary?.byBranch ?? []}
            loading={isFetching}
            emptyMessage={t(
              "company:payrolls.no_branch_data_for_selected_period",
            )}
          />
        </div>

        <div className="rounded-xs border border-border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-gray-900">
              {t("company:payrolls.by_payment_method")}
            </span>
          </div>
          <Table<IPaymentMethodRow>
            columns={methodColumns}
            data={summary?.byPaymentMethod ?? []}
            loading={isFetching}
            emptyMessage={t("company:payrolls.no_payment_method_data")}
          />
        </div>
      </div>
    </div>
  );
};
